import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { adminService } from "../services/adminService";

const sections = [
  { to: "/admin/users", title: "Users & roles", desc: "Manage roles, including admin and operator access." },
  { to: "/admin/devices", title: "Devices", desc: "Reassign device ownership and review linked devices." },
  { to: "/admin/faqs", title: "FAQs", desc: "Create, edit, and remove FAQ entries." },
  { to: "/admin/stories", title: "Stories", desc: "Manage farmer stories and story media links." },
  { to: "/admin/home-modules", title: "Home modules", desc: "Manage public home feature modules." },
  { to: "/admin/pricing-plans", title: "Pricing plans", desc: "Manage public pricing plans." },
  { to: "/admin/future", title: "Roadmap", desc: "Manage future updates and near-term focus." },
  { to: "/admin/contact", title: "Contact", desc: "Manage contact page content." },
  { to: "/admin/notifications", title: "Push notifications", desc: "Send offers, announcements, and test app push alerts." },
  { to: "/super-admin/contact-responses", title: "Contact requests", desc: "Track incoming requests and resolution status." },
  { to: "/super-admin/audit", title: "Audit logs", desc: "Review admin/super-admin actions." },
  { to: "/super-admin/server-logs", title: "Server logs", desc: "Review critical backend and terminal/runtime errors." },
  { to: "/super-admin/device-pool", title: "Device ID pool", desc: "Register device IDs before users can link them." },
  { to: "/super-admin/firmware", title: "Device firmware", desc: "OTA rollout dashboard — notify users, track accept/reject/applied." },
];

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState({
    users: 0,
    devices: 0,
    faqs: 0,
    stories: 0,
    homeModules: 0,
    pricingPlans: 0,
    roadmap: 0,
    contactResponses: 0,
    auditLogs: 0,
    serverLogs: 0,
  });
  const [recentFaqs, setRecentFaqs] = useState([]);
  const [recentStories, setRecentStories] = useState([]);
  const [recentContactRequests, setRecentContactRequests] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [users, devices, faqs, stories, homeModules, pricingPlans, future, contact, audit, serverLogs] = await Promise.all([
          adminService.getUsers(),
          adminService.getDevices(),
          adminService.listFaqs(),
          adminService.listStories(),
          adminService.listHomeModules(),
          adminService.listPricingPlans(),
          adminService.getFutureItemsAdmin(),
          adminService.listContactResponsesAdmin(),
          adminService.listAuditLogs(),
          adminService.listServerLogs(),
        ]);
        if (!mounted) return;
        setStats({
          users: users.ok ? (users.data?.items || []).length : 0,
          devices: devices.ok ? (devices.data?.items || []).length : 0,
          faqs: faqs.ok ? (faqs.data?.items || []).length : 0,
          stories: stories.ok ? (stories.data?.items || []).length : 0,
          homeModules: homeModules.ok ? (homeModules.data?.items || []).length : 0,
          pricingPlans: pricingPlans.ok ? (pricingPlans.data?.items || []).length : 0,
          roadmap: future.ok ? (future.data?.items || []).length : 0,
          contactResponses: contact.ok ? (contact.data?.items || []).length : 0,
          auditLogs: audit.ok ? (audit.data?.items || []).length : 0,
          serverLogs: serverLogs.ok ? (serverLogs.data?.items || []).length : 0,
        });
        setRecentFaqs((faqs.ok ? faqs.data?.items : [])?.slice(0, 3) || []);
        setRecentStories((stories.ok ? stories.data?.items : [])?.slice(0, 3) || []);
        setRecentContactRequests((contact.ok ? contact.data?.items : [])?.slice(0, 4) || []);
      } catch (_e) {
        // Keep zero stats if API fails.
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const statByTitle = {
    "Users & roles": stats.users,
    Devices: stats.devices,
    FAQs: stats.faqs,
    Stories: stats.stories,
    "Home modules": stats.homeModules,
    "Pricing plans": stats.pricingPlans,
    Roadmap: stats.roadmap,
    Contact: stats.contactResponses,
    "Contact requests": stats.contactResponses,
    "Push notifications": stats.users,
    "Audit logs": stats.auditLogs,
    "Server logs": stats.serverLogs,
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Super Admin"
        title="Super Admin"
        highlight="dashboard."
        description="Central access point for all admin and super-admin controls."
      />
      <PageSection variant="content">
        <div className="mx-auto max-w-6xl grid gap-6">
          <div className="grid md:grid-cols-2 gap-6">
          {sections.map((s) => (
            <Link key={s.to} to={s.to}>
              <Card className="shadow-soft hover:-translate-y-0.5 transition-all">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-3">
                    <span>{s.title}</span>
                    <span className="text-xs rounded-full border border-border px-2 py-1 text-muted-foreground">
                      {statByTitle[s.title] ?? 0}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{s.desc}</CardContent>
              </Card>
            </Link>
          ))}
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="shadow-soft">
              <CardHeader><CardTitle>Recent FAQs</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                {recentFaqs.map((f) => <div key={f._id}>{f.question}</div>)}
                {!recentFaqs.length ? <div>No FAQ data loaded.</div> : null}
              </CardContent>
            </Card>
            <Card className="shadow-soft">
              <CardHeader><CardTitle>Recent Stories</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                {recentStories.map((s) => <div key={s._id}>{s.title}</div>)}
                {!recentStories.length ? <div>No story data loaded.</div> : null}
              </CardContent>
            </Card>
            <Card className="shadow-soft">
              <CardHeader><CardTitle>Recent Contact Requests</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                {recentContactRequests.map((r) => (
                  <div key={r._id} className="flex items-center justify-between gap-3">
                    <span className="truncate">{r.subject || "Message"} · {r.phone || r.email || r.name || "unknown"}</span>
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-1 border ${
                      r.status === "resolved" ? "border-success text-success" : r.status === "contacted" ? "border-accent text-accent" : "border-destructive text-destructive"
                    }`}>{r.status || "new"}</span>
                  </div>
                ))}
                {!recentContactRequests.length ? <div>No contact requests loaded.</div> : null}
                <Link to="/super-admin/contact-responses" className="inline-flex text-accent">Open contact requests →</Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageSection>
    </PageShell>
  );
}

